import { useSyncExternalStore } from 'react';

import { currentProfileId } from '../lib/meta';

import { subscribeWorkspaceIdScope } from './workspaceIdScope';

const listeners = new Set();
let initialized = false;
let cache = Object.create(null);
let frozenSnapshot = cache;
let activeProfileId = null;

const TEXT_SUFFIX = ':text';
const STORAGE_PREFIX_BASE = 'richdown-composer';

function notify() {
  listeners.forEach((listener) => listener());
}

function getLocalStorage() {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

// Always re-derive so a profile switch routes reads/writes to the new
// per-profile bucket immediately (prior behavior cached the first value).
function deriveStoragePrefix(profileId = currentProfileId()) {
  const authorId = profileId;
  if (!authorId) throw new Error('currentProfileId is required for draft storage');
  return `${STORAGE_PREFIX_BASE}:${authorId}:`;
}

function parseDraftValue(rawValue) {
  if (rawValue == null) return '';
  try {
    return JSON.parse(rawValue);
  } catch {
    return rawValue;
  }
}

function extractPersistenceId(key, prefix) {
  if (!key || !key.startsWith(prefix) || !key.endsWith(TEXT_SUFFIX)) {
    return null;
  }

  const persistenceId = key.slice(prefix.length, -TEXT_SUFFIX.length);
  return persistenceId || null;
}

function loadAllDraftsFromStorage(profileId = currentProfileId()) {
  const storage = getLocalStorage();
  if (!storage) return Object.create(null);

  const prefix = deriveStoragePrefix(profileId);
  const result = Object.create(null);

  try {
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);
      const persistenceId = extractPersistenceId(key, prefix);

      if (!persistenceId) continue;

      const rawValue = storage.getItem(key);
      const value = parseDraftValue(rawValue);
      if (value) {
        result[persistenceId] = value;
      }
    }
  } catch {
    return Object.create(null);
  }

  return result;
}

function setSnapshot(nextCache) {
  cache = nextCache;
  frozenSnapshot = Object.freeze({ ...nextCache });
  notify();
}

function ensureInitialized() {
  if (initialized) return;
  initialized = true;
  activeProfileId = currentProfileId();

  const drafts = loadAllDraftsFromStorage(activeProfileId);
  setSnapshot(drafts);

  window.addEventListener('storage', handleStorageEvent);
  subscribeWorkspaceIdScope((_workspaceId, detail) => {
    const nextProfileId = detail?.profileId ?? currentProfileId();
    if (nextProfileId === activeProfileId) return;
    activeProfileId = nextProfileId;
    syncFromStorage();
  });
}

function handleStorageEvent(event) {
  const prefix = deriveStoragePrefix(activeProfileId);
  const persistenceId = extractPersistenceId(event.key, prefix);

  if (!persistenceId) return;

  syncFromStorage();
}

function syncFromStorage() {
  const drafts = loadAllDraftsFromStorage(activeProfileId);
  setSnapshot(drafts);
}

// Use activeProfileId (not currentProfileId()) so writes land in the same
// bucket the cache was loaded from — otherwise a mid-switch write can split
// reads/writes across two profiles.
function getDraftKey(persistenceId) {
  const prefix = deriveStoragePrefix(activeProfileId);
  return `${prefix}${persistenceId}${TEXT_SUFFIX}`;
}

export function subscribe(listener) {
  ensureInitialized();
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getSnapshot() {
  ensureInitialized();
  return frozenSnapshot;
}

function updateStorageItem(persistenceId, value) {
  const storage = getLocalStorage();
  if (!storage) return;

  try {
    if (value) {
      storage.setItem(getDraftKey(persistenceId), JSON.stringify(value));
    } else {
      storage.removeItem(getDraftKey(persistenceId));
    }
  } catch {
    // ignore storage errors (quota, privacy mode, etc.)
  }
}

export function setDraft(persistenceId, value) {
  if (!persistenceId) return;
  ensureInitialized();

  const draftValue = value ?? '';
  const nextCache = { ...cache, [persistenceId]: draftValue };
  setSnapshot(nextCache);

  updateStorageItem(persistenceId, draftValue);
}

export function clearDraft(persistenceId) {
  if (!persistenceId) return;
  ensureInitialized();

  if (!(persistenceId in cache)) return;

  const nextCache = { ...cache };
  delete nextCache[persistenceId];
  setSnapshot(nextCache);

  updateStorageItem(persistenceId, null);
}

export function getDraftIdsWithContent() {
  ensureInitialized();
  return Object.keys(frozenSnapshot);
}

export function useDraftStore() {
  ensureInitialized();
  return useSyncExternalStore(subscribe, getSnapshot, () => ({}));
}

export function useDraft(persistenceId) {
  const drafts = useDraftStore();
  return persistenceId ? (drafts[persistenceId] ?? '') : '';
}
